import { useEffect, useRef, useState } from 'react';

const DEFAULT_INTERVAL_MS = 3000;

export interface UsePolledQueryOptions<T> {
  /** Fetches the latest value. Called once per tick while polling is active. */
  fetchFn: () => Promise<T>;
  /** Polling runs only while this is true; it is re-read on every render, so the
   * caller can derive it from the latest value `onData` handed it. */
  shouldPoll: boolean;
  /** Called with the result of a tick, but never for one a later tick already
   * superseded - a slow, stale response can never overwrite fresher data. Left
   * out when the fetch itself is what updates the caller, as a refetch does. */
  onData?: (data: T) => void;
  /** Delay between ticks, in ms. Defaults to 3000. */
  intervalMs?: number;
}

export interface UsePolledQueryResult {
  /** True while a tick is armed, i.e. `shouldPoll` is true and the tab is visible. */
  isPolling: boolean;
}

/**
 * Polls `fetchFn` on a fixed interval while `shouldPoll` is true. The interval is
 * torn down (and no longer ticks) once `shouldPoll` turns false, on unmount, and
 * while the browser tab is hidden - it resumes automatically when the tab becomes
 * visible again.
 */
function usePolledQuery<T>({
  fetchFn,
  shouldPoll,
  onData,
  intervalMs = DEFAULT_INTERVAL_MS,
}: UsePolledQueryOptions<T>): UsePolledQueryResult {
  const [isVisible, setIsVisible] = useState(
    () => document.visibilityState !== 'hidden',
  );
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  const onDataRef = useRef(onData);
  onDataRef.current = onData;
  const sequenceRef = useRef(0);
  const isMountedRef = useRef(true);

  // Re-armed on every mount, not only at declaration: StrictMode mounts each
  // effect, tears it down and mounts it again, so a flag that is merely cleared
  // on teardown would disarm `onData` for the rest of the component's life.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () =>
      setIsVisible(document.visibilityState !== 'hidden');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const isPolling = shouldPoll && isVisible;

  useEffect(() => {
    if (!isPolling) {
      return undefined;
    }

    const tick = () => {
      const sequence = sequenceRef.current + 1;
      sequenceRef.current = sequence;

      fetchFnRef
        .current()
        .then((result) => {
          // A newer tick may have started - and even resolved - while this one was
          // in flight; a response that arrives out of order must never win.
          if (isMountedRef.current && sequence === sequenceRef.current) {
            onDataRef.current?.(result);
          }
        })
        // A tick that fails is simply skipped: the next one is seconds away, and
        // an unhandled rejection would surface as a crash overlay instead.
        .catch(() => undefined);
    };

    const timer = setInterval(tick, intervalMs);
    return () => clearInterval(timer);
  }, [isPolling, intervalMs]);

  return { isPolling };
}

export default usePolledQuery;
